import { PIPELINE_CATEGORY_LABEL, type TaskDetail } from '@pm/schemas';
import { LabelCaps, Select } from '@pm/ui';
import { api } from '@/lib/api';
import { moveToStage, usePipelines } from '@/lib/pipelines';
import { useTaskMutation } from '@/lib/queries';
import { useToast } from '@/lib/toast';

/**
 * Pipeline e coluna da demanda (só demanda mãe, não pessoal). Trocar de coluna usa a mesma
 * regra do quadro: mudar de categoria inicia, conclui ou pausa a demanda (a API decide).
 */
export function PipelinePanel({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const { data: pipelines = [], isLoading } = usePipelines();
  const manual = pipelines.filter((p) => p.source === 'MANUAL');
  const current = task.pipeline ? manual.find((p) => p.id === task.pipeline!.id) : undefined;

  const change = useTaskMutation((pipelineId: string | null) =>
    api.patch<TaskDetail>(`/tasks/${task.id}`, { pipeline_id: pipelineId }),
  );
  const move = useTaskMutation((stageId: string) => moveToStage(task.id, stageId));

  // Sem pipelines visíveis e fora de qualquer um: nada a mostrar.
  if (!isLoading && manual.length === 0 && !task.pipeline) return null;

  const canChange = task.can.move_pipeline;
  return (
    <div className="flex flex-col gap-1.5">
      <LabelCaps>Pipeline</LabelCaps>
      {canChange && (current || !task.pipeline) ? (
        <Select
          aria-label="Pipeline"
          value={task.pipeline?.id ?? ''}
          disabled={change.isPending}
          onChange={(e) =>
            change.mutate(e.target.value || null, {
              onSuccess: () => toast.success('Pipeline alterado'),
              onError: (err) => toast.error(err),
            })
          }
          className="h-control-sm text-sm"
        >
          <option value="">Geral (sem pipeline)</option>
          {manual.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      ) : (
        <span className="text-sm">{task.pipeline?.name ?? 'Geral'}</span>
      )}
      {task.pipeline ? (
        current && task.can.move_pipeline ? (
          <Select
            aria-label="Coluna"
            value={task.pipeline.stage?.id ?? ''}
            disabled={move.isPending}
            onChange={(e) =>
              move.mutate(e.target.value, {
                onSuccess: (t) =>
                  toast.success(`Coluna: ${(t as TaskDetail).pipeline?.stage?.name ?? ''}`),
                onError: (err) => toast.error(err),
              })
            }
            className="h-control-sm text-sm"
          >
            {current.stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {PIPELINE_CATEGORY_LABEL[s.category].toLowerCase()}
              </option>
            ))}
          </Select>
        ) : (
          <span className="flex items-center gap-1.5 text-sm text-text-2">
            <span
              className="size-2 rounded-full"
              style={{ background: task.pipeline.stage?.color ?? 'var(--text-3)' }}
            />
            {task.pipeline.stage?.name ?? '—'}
          </span>
        )
      ) : null}
    </div>
  );
}
